import React, { useEffect, useState } from "react";
import { Box, Button, ButtonBase, Chip, CircularProgress, Collapse, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { toast } from "react-toastify";
import api from "../../api";
import { DRIVER_LABELS, THEME_LABELS, TUTOR_LABELS, describeStyle, voiceListsFor } from "./styleOptions";

let settingsRequest = null;

/** Settings → Videos (saved values and option lists), loaded once per page visit. */
export function loadVideoSettings() {
  settingsRequest ??= api
    .get("admin/analytics/video-settings")
    .then((res) => res.data?.data || {})
    .catch(() => {
      settingsRequest = null;
      return {};
    });
  return settingsRequest;
}

const PRESENTERS = ["shino", "fumiriya", "shibu", "bibi", "vita", "victoria", "robot", "sam", "maya", "leo", "ava"];
const MIXES = { anime_mix: "Anime mix", mix: "Doodle mix" };
const presenterImg = (t) => `/video-styles/presenters/${t}.jpg`;
const designImg = (t) => `/video-styles/designs/${t}.jpg`;
// Settings may not have loaded yet: no presenter means no name.
const shortName = (t) => String(TUTOR_LABELS[t] || t || "").replace(/^(3D anime|Doodle): /, "").replace(/ \(.*$/, "");

/**
 * What the video will look like: the board design with the presenter in its corner.
 * A mix shows one presenter of the mix (each question gets its own).
 */
export function StylePreview({ tutor, theme, width = 300 }) {
  const shown = MIXES[tutor] ? (tutor === "mix" ? "sam" : "shino") : tutor;
  const height = Math.round((width * 9) / 16);
  return (
    <Box sx={{ position: "relative", width, height, borderRadius: 1.5, overflow: "hidden", border: "1px solid #e5e7eb", flexShrink: 0 }}>
      <Box component="img" src={designImg(theme || "classic")} alt="" sx={{ width: "100%", height: "100%", display: "block" }} />
      {shown && PRESENTERS.includes(shown) && (
        <Box
          component="img"
          src={presenterImg(shown)}
          alt=""
          sx={{ position: "absolute", left: 4, bottom: 4, height: height * 0.42, borderRadius: 1, border: "2px solid #fff", boxShadow: "0 2px 6px rgba(0,0,0,0.25)" }}
        />
      )}
      <Chip
        size="small"
        label={[MIXES[tutor] || shortName(tutor), THEME_LABELS[theme]?.split(" (")[0] || theme].filter(Boolean).join(" · ")}
        sx={{ position: "absolute", right: 6, top: 6, bgcolor: "rgba(255,255,255,0.92)", fontWeight: 600 }}
      />
    </Box>
  );
}

function Tile({ selected, onClick, img, label, wide }) {
  return (
    <Tooltip title={label}>
      <ButtonBase
        onClick={onClick}
        sx={{
          flexDirection: "column",
          width: wide ? 112 : 64,
          p: 0.5,
          borderRadius: 1.5,
          border: "2px solid",
          borderColor: selected ? "primary.main" : "transparent",
          bgcolor: selected ? "primary.50" : "#f8fafc",
        }}
      >
        {img ? (
          <Box component="img" src={img} alt="" sx={{ width: "100%", height: wide ? 60 : 64, objectFit: "cover", borderRadius: 1 }} />
        ) : (
          <Box sx={{ width: "100%", height: wide ? 60 : 64, display: "grid", placeItems: "center", fontSize: 12, color: "text.secondary" }}>
            {label}
          </Box>
        )}
        <Typography variant="caption" noWrap sx={{ maxWidth: "100%", fontWeight: selected ? 700 : 400 }}>
          {label}
        </Typography>
      </ButtonBase>
    </Tooltip>
  );
}

function Choice({ value, current, onPick, children }) {
  return (
    <Chip
      label={children}
      clickable
      color={current === value ? "primary" : "default"}
      variant={current === value ? "filled" : "outlined"}
      onClick={() => onPick(value)}
    />
  );
}

/**
 * Optional style for one generate or schedule run: presenter, board design, voice and music,
 * picked from pictures and buttons with a live preview. Anything left on "From Settings" uses
 * Admin Settings → Videos. `value` is the style object sent to the API ({} = all from Settings).
 */
export default function StyleFields({ value, onChange, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [settings, setSettings] = useState({});
  const [hearing, setHearing] = useState(null);
  const [audio, setAudio] = useState(null);
  const options = settings.options || {};

  useEffect(() => {
    let alive = true;
    loadVideoSettings().then((s) => alive && setSettings(s));
    return () => {
      alive = false;
    };
  }, []);

  const pick = (field) => (v) => {
    const next = { ...value, [field]: v === undefined || v === value[field] ? undefined : v };
    // Voices belong to a provider; a new provider starts from its default voices.
    if (field === "driver") {
      delete next.voice_female;
      delete next.voice_male;
    }
    Object.keys(next).forEach((k) => next[k] === undefined && delete next[k]);
    onChange(next);
  };
  const setVoice = (field) => (e) => pick(field)(e.target.value || undefined);

  const tutor = value.tutor ?? settings.tutor;
  const theme = value.board_theme ?? settings.board_theme ?? "classic";
  const driver = value.driver;
  const [female, male] = voiceListsFor(driver, options);

  const hear = async (who) => {
    setHearing(who);
    setAudio(null);
    try {
      const res = await api.post("admin/analytics/video-settings/preview", {
        tutor: who === "girl" ? "shino" : "fumiriya",
        subject: "maths",
        style: { driver: value.driver, voice_female: value.voice_female, voice_male: value.voice_male },
      });
      setAudio(res.data?.data?.audio || null);
    } catch (e) {
      toast.error(e.response?.data?.message || "The voice service did not return audio.");
    } finally {
      setHearing(null);
    }
  };

  return (
    <Box mt={2}>
      <Button size="small" startIcon={<TuneIcon />} onClick={() => setOpen((o) => !o)}>
        {open ? "Hide style" : "Style (optional)"}
      </Button>
      {!open && (
        <Typography variant="caption" color="text.secondary" display="block">
          {describeStyle(value) || "Presenter, design, voice and music from Settings → Videos."}
        </Typography>
      )}
      <Collapse in={open}>
        <Stack spacing={1.5} mt={1}>
          <StylePreview tutor={tutor} theme={theme} width={340} />

          <Box>
            <Typography variant="caption" color="text.secondary">Presenter</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
              <Tile label="From Settings" selected={!value.tutor} onClick={() => pick("tutor")(undefined)} />
              {Object.entries(MIXES).map(([k, l]) => (
                <Tile key={k} label={l} selected={value.tutor === k} onClick={() => pick("tutor")(k)} />
              ))}
              {PRESENTERS.map((t) => (
                <Tile key={t} label={shortName(t)} img={presenterImg(t)} selected={value.tutor === t} onClick={() => pick("tutor")(t)} />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">Board design</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
              <Tile wide label="From Settings" selected={!value.board_theme} onClick={() => pick("board_theme")(undefined)} />
              {(options.board_themes || ["classic", "chalkboard", "pastel", "night"]).map((t) => (
                <Tile
                  key={t}
                  wide
                  label={THEME_LABELS[t]?.split(" (")[0] || t}
                  img={designImg(t)}
                  selected={value.board_theme === t}
                  onClick={() => pick("board_theme")(t)}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">Voice</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
              <Choice value={undefined} current={driver} onPick={() => pick("driver")(undefined)}>
                From Settings{settings.driver ? ` (${settings.driver})` : ""}
              </Choice>
              {(options.drivers || []).map((d) => (
                <Choice key={d} value={d} current={driver} onPick={pick("driver")}>
                  {(DRIVER_LABELS[d] || d).split(" (")[0]}
                </Choice>
              ))}
            </Stack>
            {driver && (
              <Stack direction="row" spacing={1} mt={1}>
                <TextField select size="small" fullWidth label="Girl voice" value={value.voice_female || ""} onChange={setVoice("voice_female")}
                  SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Default</MenuItem>
                  {female.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </TextField>
                <TextField select size="small" fullWidth label="Boy voice" value={value.voice_male || ""} onChange={setVoice("voice_male")}
                  SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Default</MenuItem>
                  {male.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </TextField>
              </Stack>
            )}
            <Stack direction="row" spacing={1} mt={1} alignItems="center">
              <Button size="small" variant="outlined" startIcon={<VolumeUpIcon />} disabled={Boolean(hearing)} onClick={() => hear("girl")}>
                Hear girl voice
              </Button>
              <Button size="small" variant="outlined" startIcon={<VolumeUpIcon />} disabled={Boolean(hearing)} onClick={() => hear("boy")}>
                Hear boy voice
              </Button>
              {hearing && <CircularProgress size={18} />}
            </Stack>
            {audio && <audio controls autoPlay src={audio} style={{ width: "100%", marginTop: 8 }} />}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">Music</Typography>
            <Stack direction="row" gap={0.5} mt={0.5}>
              <Choice value={undefined} current={value.music_enabled} onPick={() => pick("music_enabled")(undefined)}>From Settings</Choice>
              <Choice value={true} current={value.music_enabled} onPick={pick("music_enabled")}>On</Choice>
              <Choice value={false} current={value.music_enabled} onPick={pick("music_enabled")}>Off</Choice>
            </Stack>
          </Box>

          <Typography variant="caption" color="text.secondary">
            The cost above uses the voice provider in Settings; the queued message shows the cost for this style.
          </Typography>
        </Stack>
      </Collapse>
    </Box>
  );
}
